import {
  Box,
  Checkbox,
  MenuItem,
  type SelectChangeEvent,
  styled,
} from '@mui/material';
import { Close } from '@tempo-io/sui-icons';
import { useState } from 'react';
import { Badge } from './Badge';
import { Select, type SelectProps } from './Select';

type MultiSelectProps = SelectProps & {
  isOptionSelected: (value: string | number) => boolean;
  limitTags?: boolean;
  placeholder?: string;
};

export const StyledChip = styled(Badge)(() => ({
  minWidth: 70,
  justifyContent: 'space-between',
}));

export const MultiSelect = (props: MultiSelectProps) => {
  const { menuItems, isOptionSelected, onChange, placeholder, limitTags, ...selectProps } = props;

  const [showSelect, setShowSelect] = useState<boolean>(false);

  const handleChange = (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderAllTags = (value: unknown) => {
    if (!menuItems) return null;
    if (!value || (Array.isArray(value) && value.length === 0))
      return <span className="text-sm leading-normal text-text-neutral-inverted">{placeholder}</span>;

    if (Array.isArray(value)) {
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {value.map((val) => {
            const item = menuItems.find((el) => el.value === val);
            const handleDelete = () => {
              if (onChange) {
                onChange(
                  {
                    target: { value: value.filter((el) => el !== val) },
                  } as SelectChangeEvent<unknown>,
                  null,
                );
              }
              return;
            };

            if (!item) return null;
            return (
              <Badge
                key={item.value}
                label={item.label}
                size="small"
                deleteIcon={
                  <span
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Close />
                  </span>
                }
                onDelete={handleDelete}
              />
            );
          })}
        </Box>
      );
    }
    return <span className="text-sm leading-normal">{value.toString()}</span>;
  };

  const renderOneTag = (value: unknown) => {
    if (!menuItems) return null;
    if (!value || (Array.isArray(value) && value.length === 0))
      return <span className="text-sm leading-normal text-[lightgray]">{placeholder}</span>;

    if (Array.isArray(value)) {
      let moreText = '';
      if (value.length > 1) {
        moreText = ` +${value.length - 1}`;
      }
      const item = menuItems.find((el) => el.value === value[0]);
      if (!item) return null;

      return (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Badge label={item.label} size="small" />
          {moreText}
        </Box>
      );
    }
    return <span className="text-sm leading-normal">{value.toString()}</span>;
  };

  return (
    <Select
      multiple
      open={showSelect}
      onOpen={() => setShowSelect(true)}
      onClose={() => setShowSelect(false)}
      onChange={handleChange}
      displayEmpty
      renderValue={menuItems ? (limitTags ? renderOneTag : renderAllTags) : undefined}
      {...selectProps}
    >
      {menuItems?.map((item) => {
        return (
          <MenuItem key={item.value} value={item.value} data-testid={`options-${item.value}`}>
            <Checkbox checked={isOptionSelected(item.value)} />
            {item.label}
          </MenuItem>
        );
      })}
      {props.children}
    </Select>
  );
};
